import axios from 'axios';
import * as Sentry from '@sentry/browser';
import Lock from 'browser-tabs-lock';
import AuthenticationUtil from 'utils/AuthenticationUtil';
import { postGarminEvent } from 'Analytic/ClientAnalytics';
import { gcsAnalyticsRequestKey } from '../backbone/models/preferences/PreferencesMapping';
import { isModern, CONTEXT } from 'utils/NavigationUtil';
import { getCSRFToken, isNewSessionEnabled } from 'auth/SessionUtil';
import { isRaceManagementMode, isReadonlyMode } from 'system/SystemUtil';
import { isHarmonyConnect, notifyTokenExpired } from 'utils/harmonyConnectApp/harmonyConnectAppUtil';

class DiTokenManager {
    /**
     * Timeout duration for network requests based on the hostname.
     * If the hostname is 'connect.garmin.com' or 'connect.garmin.cn', the timeout is set to 10 seconds.
     * For all other hostnames, the timeout is set to 30 seconds.
     */
    timeoutDuration = ['connect.garmin.com', 'connect.garmin.cn'].includes(window.location.hostname) ? 10000 : 30000;
    expiryAdjustment = 60 * 1000;
    waitingForReload = false;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: '/',
            timeout: this.timeoutDuration
        });
        delete this.axiosInstance.defaults.xsrfHeaderName;
        delete this.axiosInstance.defaults.xsrfCookieName;

        this.axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                if (error.response && error.response.status === 401) {
                    // Session is invalid, redirect to SSO sign in page to refresh session
                    AuthenticationUtil.toSSOSignin();

                    // Axios interceptors are retruning early before the Sign Out happens
                    const delay = () => new Promise((resolve) => setTimeout(resolve, 1000));
                    await delay();
                }
                return Promise.reject(error);
            }
        );

        this.lock = new Lock();
    }

    async initializeAccountSharing(accountSharingTokenPromise) {
        this.accountSharingTokenPromise = accountSharingTokenPromise;
        // Remove the token in localStorage if it doesn't match the current User
        this.removeToken();

        if (!this.accessToken) {
            // console.log('Fetching token to initialze DI Token Manager');
            try {
                await this.getToken();
            } catch (error) {
                Sentry.captureException(error);
                throw new Error();
            }
        }
    }

    async initialize(viewerIsAuthenticated, garminGuid) {
        this.viewerIsAuthenticated = viewerIsAuthenticated;

        // Remove the token in localStorage if it doesn't match the current User
        if (this.token && this.garminGuid !== garminGuid) {
            this.removeToken();
        }

        if (!this.accessToken) {
            // // console.log('Fetching token to initialze DI Token Manager');
            try {
                await this.getToken('initialize');
            } catch (error) {
                Sentry.captureException(error);
            }
        }
    }

    async exchangeAccountSharingToken() {
        try {
            let response = await this.accountSharingTokenPromise();
            let token = response.data;
            token.expires = Date.now() + token.expires_in * 1000 - this.expiryAdjustment;
            token.refresh_token_expires = Date.now() + token.refresh_token_expires_in * 1000 - this.expiryAdjustment;
            this.token = token;

            return this.token.access_token;
        } catch (e) {
            throw e;
        }
    }

    async exchangeOAuth1Token() {
        try {
            // console.log("Exchanging OAuth 1.0 token for DI OAuth")

            const resp = await this.axiosInstance.post('/modern/di-oauth/exchange');

            const token = resp.data;
            token.expires = Date.now() + token.expires_in * 1000 - this.expiryAdjustment;
            token.refresh_token_expires = Date.now() + token.refresh_token_expires_in * 1000 - this.expiryAdjustment;

            this.token = token;

            try {
                this.axiosInstance.get(`${CONTEXT}/preference/${gcsAnalyticsRequestKey}`).then(function(response) {
                    if (response?.data?.value && response.data.value !== 'false') {
                        postGarminEvent('SESSION');
                    }
                });
            } catch (e) {
                console.log(e);
            }

            return token.access_token;
        } catch (error) {
            if (error.response && error.response.status === 409) {
                console.log('Reload to update session');
                this.removeToken();
                await this.lock.releaseLock('fetchToken');
                window.location.reload();
                const delay = () => new Promise((resolve) => setTimeout(resolve, 5000));
                await delay();
            } else {
                if (error.config) {
                    throw this.addErrorContext(error);
                }
                throw error;
            }
        }
    }

    async fetchPublicToken() {
        try {
            // // console.log('Fetching a token for public endpoints');
            const resp = await this.axiosInstance.post('/services/auth/token/public');

            const token = resp.data;
            token.expires = Date.now() + token.expires_in * 1000 - this.expiryAdjustment;

            this.token = token;
            return token.access_token;
        } catch (error) {
            if (error.config) {
                throw this.addErrorContext(error);
            }
            throw error;
        }
    }

    async fetchTokenUsingRefresh() {
        try {
            const data = {
                refresh_token: this.refreshToken
            };
            const resp = await fetch('/services/auth/token/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Connect-Csrf-Token': getCSRFToken()
                },
                body: JSON.stringify(data)
            });

            const token = await resp.json();
            token.expires = Date.now() + token.expires_in * 1000 - this.expiryAdjustment;
            token.refresh_token_expires = Date.now() + token.refresh_token_expires_in * 1000 - this.expiryAdjustment;

            this.token = token;
            return token.access_token;
        } catch (error) {
            if (error.config) {
                throw this.addErrorContext(error);
            }
            throw error;
        }
    }

    async fetchToken() {
        if (await this.lock.acquireLock('fetchToken', this.timeoutDuration)) {
            // lock has been acquired
            try {
                // Check for the token again because it might have been fetched while waiting for the lock
                if (this.accessToken) {
                    return this.accessToken;
                }

                if (this.refreshToken) {
                    return await this.fetchTokenUsingRefresh();
                }

                if (this.accountSharingTokenPromise) {
                    return await this.exchangeAccountSharingToken();
                } else if (isModern() && this.viewerIsAuthenticated) {
                    return await this.exchangeOAuth1Token();
                } else {
                    return await this.fetchPublicToken();
                }
            } catch (e) {
                throw e;
            } finally {
                await this.lock.releaseLock('fetchToken');
            }
        } else {
            throw new Error('Timeout acquiring lock');
        }
    }

    redirectToSigninPage() {
        // If the user is on Harmony Connect, notify that the token has expired instead of redirecting to the signin page
        // Harmony connect will handle the expired
        if (isHarmonyConnect()) {
            notifyTokenExpired(window.location.href);
            return;
        }
        window.location.replace(window.location.origin + '/signin');
    }

    removeToken() {
        localStorage.removeItem('token');
        this.removeLocalConfig();
    }

    removeLocalConfig() {
        localStorage.removeItem('viewerSocialProfile');
        localStorage.removeItem('viewerUserPreferences');
        localStorage.removeItem('actorProfileImage');
    }

    addErrorContext(error) {
        // Create new Error that contains the URL from the API call
        let wrappedError = new Error(`${error.message} - ${error.config.url}`);

        // Add custom Axios fields back to the new Error
        wrappedError.config = error.config;
        wrappedError.request = error.request;
        wrappedError.response = error.response;

        return wrappedError;
    }

    async getToken() {
        // Throw Error to make sure this isn't used if the new session is enabled
        if (isNewSessionEnabled()) {
            const error = new Error('New session is enabled.  getToken is disabled.');
            console.error(error);
            throw error;
        }

        // Return the token from storage if there is a valid one
        if (this.accessToken) {
            return this.accessToken;
        }

        // singlePromise is used to make sure that we only have 1 call fetching a token at a time regardless of the number of concurrent API requests
        // It internally keeps track of a promise and will return the same promise to additional calls until the promise resolves
        const singlePromise = (callback) => {
            if (!this.getTokenPromise) {
                this.getTokenPromise = callback().finally(() => {
                    this.getTokenPromise = null;
                });
            }
            return this.getTokenPromise;
        };

        return singlePromise(async () => {
            try {
                return await this.fetchToken();
            } catch (error) {
                Sentry.captureException(error);
                this.redirectToSigninPage();
            }
        });
    }

    get token() {
        const tokenString = localStorage.getItem('token');

        if (tokenString) {
            const token = JSON.parse(tokenString);

            // Remove the token if it is expired
            const now = Date.now();
            if (token.expires < now && (!token.refresh_token || token.refresh_token_expires < now)) {
                this.removeToken();
                return;
            }

            return token;
        }
    }

    set token(token) {
        if (token) {
            localStorage.setItem('token', JSON.stringify(token));
        }
    }

    get accessToken() {
        if (this.token && this.token.expires > Date.now()) {
            return this.token.access_token;
        }
    }

    get refreshToken() {
        if (this.token && this.token.refresh_token_expires > Date.now()) {
            return this.token.refresh_token;
        }
    }

    get garminGuid() {
        // Use the raw token since we need this value if the accessToken is expired, but the refreshToken is still valid
        if (this.token) {
            const encodedToken = this.token.access_token.split('.')[1];
            const decodedToken = atob(encodedToken); //Buffer.from(encodedToken, 'base64');
            const token = JSON.parse(decodedToken);
            return token.garmin_guid;
        }
    }

    async refreshSessionIfExpired() {
        if (window.SESSION_EXPIRES.expires < Date.now()) {
            // Access token is expired
            if (window.SESSION_EXPIRES.refreshExpires < Date.now()) {
                // Refresh is expired.  Reload the page to have the server try to start a new session.
                if (!this.waitingForReload) {
                    // location.reload() isn't async so we can't await and it should only be called once
                    this.waitingForReload = true;
                    window.location.reload();
                }
                // Return an error so we don't make the API call without a token
                throw new Error('Session is expired');
            } else {
                if (!isModern() || isReadonlyMode() || isRaceManagementMode()) {
                    await this.refreshDiOauthSession();
                } else {
                    await this.refreshSession();
                }
            }
        }
    }

    refreshSession() {
        return this.refreshSessionUrl('/modern/di-oauth/refreshToken');
    }

    refreshDiOauthSession() {
        return this.refreshSessionUrl('/services/auth/token/di-oauth/refresh');
    }

    async refreshSessionUrl(url) {
        // singlePromise is used to make sure that we only have 1 call refreshing session at a time regardless of the number of concurrent API requests
        // It internally keeps track of a promise and will return the same promise to additional calls until the promise resolves
        const singlePromise = (callback) => {
            if (!this.refreshPromise) {
                this.refreshPromise = callback().finally(() => {
                    this.refreshPromise = null;
                });
            }
            return this.refreshPromise;
        };

        return singlePromise(async () => {
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Connect-Csrf-Token': getCSRFToken()
                    }
                });

                if (!response.ok) {
                    throw new Error(`Response status: ${response.status}`);
                }

                // Update the global with the new session expiration
                const json = await response.json();
                window.SESSION_EXPIRES = json;

                return;
            } catch (error) {
                Sentry.captureException(error);
                this.redirectToSigninPage();
            }
        });
    }
}

const tokenManager = new DiTokenManager();
export default tokenManager;
